// Copyright (c) Bentley Systems

/**
 * Longest period, in days, that Debug logging can be switched on for. ProjectProvider rejects
 * anything longer.
 */
export const MAX_DEBUG_LOGGING_DAYS = 7;

/** Duration preselected when an architect opens the Debug logging form. */
export const DEFAULT_DEBUG_LOGGING_DAYS = 1;

/**
 * ProjectExecutor pods reuse ProjectProvider's answer for this many minutes, so turning Debug
 * logging on or off reaches new runs within this time.
 */
export const DEBUG_LOGGING_PROPAGATION_MINUTES = 10;

/**
 * Debug logging state of an integration project on one iTwin, as ProjectProvider reports it.
 * Timestamps are ISO 8601 UTC strings.
 */
export type ProjectDebugLogging = {
  uniqueName: string;
  iTwinId: string;
  debugEnabled: boolean;
  enabledAtUtc?: string | null;
  expiresAtUtc?: string | null;
  enabledByName?: string | null;
};

/** Duration choices offered to the architect: 1 to {@link MAX_DEBUG_LOGGING_DAYS} days. */
export const debugLoggingDurationOptions = (): { value: number; label: string }[] =>
  Array.from({ length: MAX_DEBUG_LOGGING_DAYS }, (_, i) => {
    const days = i + 1;
    return { value: days, label: days === 1 ? "1 day" : `${days} days` };
  });

/**
 * Whether Debug logging is on at `now`. ProjectProvider only reports grants that have not
 * lapsed, but a page left open can outlive one, so the expiry is checked here as well.
 */
export const isDebugLoggingActive = (
  state: ProjectDebugLogging | undefined,
  now: Date = new Date(),
): boolean => {
  if (!state?.debugEnabled) {
    return false;
  }
  if (!state.expiresAtUtc) {
    return true;
  }
  const expiresAt = Date.parse(state.expiresAtUtc);
  return Number.isNaN(expiresAt) || expiresAt > now.getTime();
};

/** Indexes the grants that are still on by project unique name. */
export const indexActiveDebugLogging = (
  states: ProjectDebugLogging[],
  now: Date = new Date(),
): Map<string, ProjectDebugLogging> =>
  new Map(
    states
      .filter((s) => isDebugLoggingActive(s, now))
      .map((s) => [s.uniqueName, s]),
  );

/** Formats a UTC timestamp from ProjectProvider in the user's local time. */
export const formatDebugLoggingTime = (utc: string | null | undefined): string =>
  utc ? new Date(utc).toLocaleString() : "";

/** Choices of the Debug logging column filter on Subscribed projects. */
export const DEBUG_LOGGING_FILTER_OPTIONS = [
  { value: "on", label: "On" },
  { value: "off", label: "Off" },
] as const;

/**
 * OData `$filter` clause for the Debug logging column filter. ProjectProvider's subscriptions do
 * not carry the Debug logging state, so the clause matches the unique names of the projects that
 * have it on, taken from the grants already loaded for the iTwin. Names are URL-encoded.
 *
 * Returns "" when the filter keeps every subscription (no filter, or Off while nothing is on), and
 * `null` when it can keep none (On while nothing is on), so the caller can skip the request.
 */
export const debugLoggingODataFilter = (
  filterValue: unknown,
  uniqueNames: readonly string[],
  uniqueNamePath: string,
): string | null => {
  if (filterValue !== "on" && filterValue !== "off") {
    return "";
  }
  if (uniqueNames.length === 0) {
    return filterValue === "on" ? null : "";
  }
  const names = uniqueNames
    .map((name) => `'${encodeURIComponent(name.replace(/'/g, "''"))}'`)
    .join(",");
  const isOn = `${uniqueNamePath} in (${names})`;
  return filterValue === "on" ? isOn : `not (${isOn})`;
};

/** One-line summary of a grant that is on, e.g. for the Subscribed projects tooltip. */
export const describeDebugLogging = (state: ProjectDebugLogging): string => {
  const until = state.expiresAtUtc
    ? ` until ${formatDebugLoggingTime(state.expiresAtUtc)}`
    : "";
  const by = state.enabledByName ? `, turned on by ${state.enabledByName}` : "";
  return `Debug logging on${until}${by}`;
};
